import { enableProdMode, LOCALE_ID, importProvidersFrom } from "@angular/core";

import { environment } from "./environments/environment";
import { DatePipe, registerLocaleData } from "@angular/common";
import localeEs from "@angular/common/locales/es";
import { BsModalService, ModalModule } from "ngx-bootstrap/modal";
import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { SecurityContextManagerModule } from "@santander/security-angular";
import { ConfigService, provideConfig } from "@ng-darwin/config";
import { firstValueFrom } from "rxjs";
import { provideLogger } from "@ng-darwin/logger";
import { SharedModule } from "./app/modules/shared/shared.module";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { AppRoutingModule } from "./app/app-routing.module";
import { FormsModule } from "@angular/forms";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { NgxPaginationModule } from "ngx-pagination";
import { provideAnimations } from "@angular/platform-browser/animations";
import { AppComponent } from "./app/app.component";

async function getConfigAsync(configService: ConfigService): Promise<any> {
  const cfg = await firstValueFrom(configService.onConfigLoaded$);
  const security = { ...(cfg as any).gluon!.security! };
  const app = (cfg as any).app;

  const isLocal = app?.environment === 'local';
  const isOcu = app?.ocuAiming === 'ocu';

  if (isLocal) {
    // redirectUri must point to localhost so the OAuth callback reaches the local dev server.
    // token/end_session go through the real backend origin extracted from app.base.
    const localOrigin = 'http://localhost:4200';
    const gatewayOrigin = new URL(app.base as string).origin;

    security.redirectUri = `${localOrigin}/oauth-callback`;
    security.serverConfig = {
      ...security.serverConfig,
      token_endpoint: `${gatewayOrigin}/gw/presc/sos/oauth/token`,
      end_session_endpoint: `${gatewayOrigin}/gw/presc/sos/oauth/logoff`,
    };
  } else if (isOcu) {
    // Override with the OCU portal domain. The PRO config.json has gruposantander.es URLs,
    // so when the green (OCU) slot is active we replace them here.
    const ocuGwOrigin = app.ocuGwOrigin;

    const ocuDnsOrigin = window.location.origin;

    security.redirectUri = `${ocuDnsOrigin}/oauth-callback`;
    security.issuerUri = `${ocuGwOrigin}/presc/sos/oauth`;
    security.serverConfig = {
      ...security.serverConfig,
      issuer: `${ocuGwOrigin}/presc/sos/oauth`,
      authorization_endpoint: `${ocuGwOrigin}/presc/sos/oauth/authorize`,
      token_endpoint: `${ocuGwOrigin}/presc/sos/oauth/token`,
      end_session_endpoint: `${ocuGwOrigin}/presc/sos/oauth/logoff`,
    };
  }

  return security;
}

if (environment.production) {
  enableProdMode();
}

registerLocaleData(localeEs);

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(SharedModule, BrowserModule, AppRoutingModule, FormsModule, NgbModule, NgxPaginationModule, ModalModule.forRoot()),
        { provide: LOCALE_ID, useValue: "es" },
        DatePipe,
        BsModalService,
        provideHttpClient(withInterceptorsFromDi()),
        importProvidersFrom(SecurityContextManagerModule.forRoot({
            useFactory: getConfigAsync,
            deps: [ConfigService],
        })),
        provideConfig({
            technicalGrouping: "f-ng-50073761-front",
            logEvent: {
                level: environment.configLogLevel,
                handler(ev) {
                    console.log("Log event from Config module:", ev);
                },
            },
            errorEvent: {
                handler(ev) {
                    console.error("Error event from Config module:", ev);
                },
            },
            configLoadedEvent: {
                handler(ev) {
                    console.log("ConfigLoaded event from Config module:", ev);
                },
            },
        }),
        provideLogger(),
        provideAnimations()
    ]
})
  .catch((err) => console.error(err));
